import { Suspense, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

import NavLayout from './nav-layout';
import SidebarComponents from './sidebar';
import { DialogProvider } from '../../components/layouts/page-with-dialog/dialog-context';

import CreateGatewayDialog from '../../features/gateways/components/create-gateway.dialog';
import HelmetTabComponent from '../../components/helmet-tab/helmet-tab';

import { setActiveGateway } from '../store/gateway.slice';
import { useGatewaysQuery, useUserProfileQuery } from '../store';

import { Gateway } from '../../types';
import { useGateway } from '../../hooks';
import {
    canAccessGatewaySettingsSection,
    getGatewayAccess,
} from '../../features/gateways/gateway-access.utils';
import {
    getGatewayLandingPath,
    getGatewaySettingsLandingPath,
} from '../../features/gateways/components/gateway-selector/gateway-switch-path';
import {
    GatewaySwitchContentFallback,
    GatewaySwitchFallbackVariant,
} from '../../components/fallbacks/gateway-switch.fallback';
import useGatewayRouteTransition from '../../features/gateways/hooks/use-gateway-route-transition';

export default function DashboardLayout() {
    const { w } = useParams<{ w: string }>();

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const activeGateway = useGateway();
    const showGatewayTransition = useGatewayRouteTransition({
        routeWebsite: w,
        activeWebsite: activeGateway?.website,
    });
    const isOverviewPage = location.pathname === '/';
    const isTicketPage =
        location.pathname === '/ticket' || location.pathname.startsWith('/ticket/');
    const isGatewayDashboardPage = Boolean(
        w &&
            (location.pathname === `/${w}` ||
                location.pathname === `/${w}/`),
    );
    const isTransactionListPage = Boolean(
        w &&
            (location.pathname === `/${w}/transaction` ||
                location.pathname === `/${w}/transaction/`),
    );
    const isTransactionDetailPage = Boolean(
        w && location.pathname.startsWith(`/${w}/transaction/`),
    );
    const isWithdrawListPage = Boolean(
        w &&
            (location.pathname === `/${w}/withdraw` ||
                location.pathname === `/${w}/withdraw/`),
    );
    const isWithdrawDetailPage = Boolean(
        w && location.pathname.startsWith(`/${w}/withdraw/`),
    );
    const isTransactionAnalyticsPage = Boolean(
        w && location.pathname === `/${w}/transaction-analytics`,
    );
    const isCustomerBehaviorAnalyticsPage = Boolean(
        w && location.pathname === `/${w}/customer-behavior-analytics`,
    );
    const isRangeReportPage = Boolean(
        w && location.pathname === `/${w}/range-report`,
    );
    const isMarketingPage = location.pathname
        .replace(/\/$/, '')
        .startsWith('/marketing/');
    const shouldHideContentScrollbar =
        isOverviewPage ||
        isGatewayDashboardPage ||
        isTransactionListPage ||
        isTransactionDetailPage ||
        isWithdrawListPage ||
        isWithdrawDetailPage ||
        isTransactionAnalyticsPage ||
        isCustomerBehaviorAnalyticsPage ||
        isRangeReportPage ||
        isMarketingPage ||
        isTicketPage ||
        location.pathname === '/report' ||
        location.pathname === '/profile/account' ||
        location.pathname === '/profile/account/' ||
        location.pathname === '/profile/addresses' ||
        location.pathname === '/profile/addresses/' ||
        location.pathname.includes('/settings/');

    const { data: gateways } = useGatewaysQuery({
        page: 1,
    });

    const { data: profile, isLoading: isProfileLoading } =
        useUserProfileQuery();

    const routeGateway =
        w && gateways?.items
            ? activeGateway?.website === w
                ? activeGateway
                : gateways.items.find((item: Gateway) => item.website === w)
            : undefined;
    const routeAccess =
        routeGateway && profile
            ? getGatewayAccess(routeGateway, profile.id)
            : undefined;
    const gatewayBasePath = w ? `/${w}` : '';
    const isDashboardPath = isGatewayDashboardPage;
    const isSettingsPath = Boolean(
        w && location.pathname.startsWith(`${gatewayBasePath}/settings`),
    );
    const routeSettingsSection = isSettingsPath
        ? location.pathname
              .slice(`${gatewayBasePath}/settings`.length)
              .split('/')
              .filter(Boolean)[0]
        : undefined;
    const gatewaySwitchFallbackVariant: GatewaySwitchFallbackVariant =
        isGatewayDashboardPage
            ? 'dashboard'
            : isTransactionAnalyticsPage || isCustomerBehaviorAnalyticsPage || isRangeReportPage
              ? 'analytics'
              : isTransactionListPage || isWithdrawListPage
                ? 'list'
                : isSettingsPath
                  ? 'settings'
                  : 'generic';
    const canRenderGatewayContent =
        !w ||
        Boolean(
            routeGateway &&
                (!isDashboardPath || routeAccess?.canEnterDashboard) &&
                (!isSettingsPath ||
                    (routeAccess &&
                        canAccessGatewaySettingsSection(
                            routeAccess,
                            routeSettingsSection,
                        ))),
        );
    const isGatewayContextReady = !w || activeGateway?.website === w;

    useEffect(() => {
        if (!w || !gateways || !profile) {
            return;
        }

        const gateway =
            activeGateway?.website === w
                ? activeGateway
                : gateways.items.find(
                      (item: Gateway) => item.website === w,
                  );

        if (!gateway) {
            navigate('/', { replace: true });
            return;
        }

        const access = getGatewayAccess(gateway, profile.id);
        const gatewayBasePath = `/${w}`;
        const isDashboardPath =
            location.pathname === gatewayBasePath ||
            location.pathname === `${gatewayBasePath}/`;
        const isSettingsPath = location.pathname.startsWith(
            `${gatewayBasePath}/settings`,
        );
        const settingsSection = isSettingsPath
            ? location.pathname
                  .slice(`${gatewayBasePath}/settings`.length)
                  .split('/')
                  .filter(Boolean)[0]
            : undefined;

        if (isDashboardPath && !access.canEnterDashboard) {
            navigate('/', { replace: true });
            return;
        }

        if (isSettingsPath) {
            if (!access.canEnterSettings) {
                navigate(getGatewayLandingPath(gateway, profile.id), {
                    replace: true,
                });
                return;
            }

            if (!canAccessGatewaySettingsSection(access, settingsSection)) {
                navigate(getGatewaySettingsLandingPath(gateway, profile.id), {
                    replace: true,
                });
                return;
            }

            if (!settingsSection) {
                navigate(getGatewaySettingsLandingPath(gateway, profile.id), {
                    replace: true,
                });
                return;
            }
        }

        if (gateway.status !== 'activated') {
            const canOpenPendingOverall =
                gateway.status === 'pending' &&
                isSettingsPath &&
                settingsSection === 'overall' &&
                access.canManageOverallSettings;

            if (!canOpenPendingOverall) {
                navigate('/', { replace: true });
                return;
            }
        }

        if (activeGateway?.id !== gateway.id) {
            dispatch(setActiveGateway(gateway));
        }
    }, [
        activeGateway,
        dispatch,
        gateways,
        location.pathname,
        navigate,
        profile,
        w,
    ]);

    useEffect(() => {
        if (isProfileLoading) {
            return;
        }

        if (profile?.status !== 'accept') {
            navigate('/', { replace: true });
        }
    }, [isProfileLoading, navigate, profile?.status]);

    return (
        <>
            <HelmetTabComponent title="پیشخوان" />

            <Box
                sx={(theme) => ({
                    display: 'flex',
                    width: '100%',
                    height: '100dvh',
                    minHeight: 0,
                    overflow: 'hidden',
                    position: 'relative',
                    isolation: 'isolate',
                    backgroundColor: 'background.default',
                    backgroundImage:
                        theme.palette.mode === 'light'
                            ? `radial-gradient(circle at 14% -10%, ${alpha(
                                  theme.palette.primary.main,
                                  0.09,
                              )} 0%, transparent 29%), radial-gradient(circle at 88% 105%, ${alpha(
                                  theme.palette.info.main,
                                  0.045,
                              )} 0%, transparent 26%)`
                            : `radial-gradient(circle at 14% -10%, ${alpha(
                                  theme.palette.primary.main,
                                  0.13,
                              )} 0%, transparent 28%), radial-gradient(circle at 88% 105%, ${alpha(
                                  theme.palette.info.main,
                                  0.055,
                              )} 0%, transparent 24%)`,
                })}
            >
                <SidebarComponents gatewaySwitching={showGatewayTransition} />

                <Box
                    component="main"
                    sx={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        overflow: 'hidden',
                        p: {
                            xs: 0,
                            md: 1.5,
                            lg: 2,
                        },
                        gap: { xs: 0, md: 1.5 },
                    }}
                >
                    <NavLayout />

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            overflowY: isTicketPage ? 'hidden' : 'auto',
                            overflowX: 'hidden',
                            scrollBehavior: 'smooth',
                            px: { xs: 1.25, sm: 2, md: 0.5 },
                            pb: isTicketPage ? 0 : { xs: 2.5, md: 1.5 },
                            scrollbarGutter: shouldHideContentScrollbar
                                ? 'auto'
                                : 'stable',
                            scrollbarWidth: shouldHideContentScrollbar
                                ? 'none'
                                : 'thin',
                            msOverflowStyle: shouldHideContentScrollbar
                                ? 'none'
                                : 'auto',
                            '&::-webkit-scrollbar': {
                                display: shouldHideContentScrollbar
                                    ? 'none'
                                    : 'block',
                                width: shouldHideContentScrollbar ? 0 : 8,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: isMarketingPage || isRangeReportPage ? 'none' : 1720,
                                mx: isMarketingPage || isRangeReportPage ? 0 : 'auto',
                                height: isTicketPage ? '100%' : 'auto',
                                minHeight: 0,
                            }}
                        >
                            {showGatewayTransition ? (
                                <GatewaySwitchContentFallback
                                    variant={gatewaySwitchFallbackVariant}
                                />
                            ) : (
                                <DialogProvider>
                                    <Suspense fallback={null}>
                                        {canRenderGatewayContent && isGatewayContextReady ? (
                                            <Outlet />
                                        ) : null}
                                    </Suspense>
                                </DialogProvider>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <CreateGatewayDialog />
        </>
    );
}
